import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";

import axios from "../utils/axios";
import { showErrorSnackbar, showSuccessSnackbar } from "../redux/slices/snackbar";

const PINCODE_PATTERN = /^[1-9][0-9]{5}$/;

/**
 * "+" button that adds a pincode to the master when it is not in the list.
 * Calls onAdded({ label, value }) with the new pincode so the caller can select it.
 */
function QuickAddPincode({ onAdded, disabled = false }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [pincode, setPincode] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const close = () => {
    if (isSaving) return;
    setOpen(false);
    setPincode("");
    setError("");
  };

  const save = async () => {
    const value = pincode.trim();
    if (!PINCODE_PATTERN.test(value)) {
      setError("Enter a valid 6 digit pincode");
      return;
    }
    try {
      setIsSaving(true);
      const response = await axios.post("/pincode/add", { pincodeName: value, status: "ACTIVE" });
      dispatch(showSuccessSnackbar("Pincode added"));
      // Same shape as the pincode auto-search: the pincode itself is the value
      const name = response.data.pincodeName;
      onAdded({ label: name, value: Number(name) });
      setOpen(false);
      setPincode("");
      setError("");
    } catch (err) {
      // Already in the master (just not loaded in the list) - select it instead
      if (err.message && err.message.toLowerCase().includes("exist")) {
        onAdded({ label: value, value: Number(value) });
        setOpen(false);
        setPincode("");
        setError("");
        return;
      }
      setError(err.message || "Failed to add pincode");
      dispatch(showErrorSnackbar(err.message || "Failed to add pincode"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <React.Fragment>
      <Tooltip title="Add pincode">
        <span>
          <IconButton color="primary" onClick={() => setOpen(true)} disabled={disabled}>
            <AddIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
        <DialogTitle>Add Pincode</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Pincode"
            value={pincode}
            error={!!error}
            helperText={error}
            inputProps={{ inputMode: "numeric", maxLength: 6 }}
            sx={{ mt: 5 }}
            onChange={(e) => {
              setPincode(e.target.value.replace(/\D/g, ""));
              setError("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // Don't let Enter submit the surrounding user form
                e.preventDefault();
                e.stopPropagation();
                save();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={save}
            disabled={isSaving}
            endIcon={isSaving ? <CircularProgress size={18} /> : null}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}

export default QuickAddPincode;
